'use client';

import { FC, ReactNode, useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, Ticket, Zap } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Voucher } from 'hotdesk_typescript';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './PromoBanner.module.scss';
export interface PromoBannerProps {
  coupons?: Voucher[];
  subscriptionApplicable?: boolean;
  suppressCoupons?: boolean;
  showCustomQuote?: boolean;
  /** Slot between coupons and custom-quote (e.g. membership offer banner). */
  membershipOffer?: ReactNode;
  onSubscriptionClick?: () => void;
  onCustomQuoteClick?: () => void;
}
const formatDiscount = (coupon: Voucher): string | undefined => {
  const c = coupon as any;
  if (c.displayDiscount) return c.displayDiscount;
  const value = c.value;
  if (value === undefined || value === null || value === '') return undefined;
  const type = c.type;
  if (type === 'percentage') return `${value}% OFF`;
  if (c.currency) return `${value} ${c.currency} OFF`;
  return `${value} OFF`;
};
const getPromoHeadline = (coupon: Voucher, discount?: string): string | undefined => {
  const c = coupon as any;
  return c.extraRules?.title || c.extraRules?.name || c.title || c.name || (c.description && c.description !== c.extraRules?.description ? c.description : undefined) || discount;
};
const PromoCard: FC<{
  coupon: Voucher;
  promoLabel: string;
}> = ({
  coupon,
  promoLabel
}) => {
  const t = useTranslations('workspace-details');
  const discount = formatDiscount(coupon);
  const headline = getPromoHeadline(coupon, discount);
  return <div className={styles.promoCard} data-sentry-component="PromoCard" data-sentry-source-file="PromoBanner.tsx">
            <span className={styles.promoOrb} aria-hidden="true" />
            <Image src="/assets/icons/morphy-banner-deco.svg" alt="" width={114} height={140} className={styles.promoMorphy} aria-hidden="true" data-sentry-element="Image" data-sentry-source-file="PromoBanner.tsx" />
            <div className={styles.promoBody}>
                <div className={styles.promoTopRow}>
                    <span className={styles.promoIcon}>
                        <Ticket size={18} strokeWidth={2} data-sentry-element="Ticket" data-sentry-source-file="PromoBanner.tsx" />
                    </span>
                    <span className={styles.promoLabel}>{promoLabel}</span>
                    <span className={styles.promoCodeChip}>{coupon.code}</span>
                </div>
                {headline && <p className={styles.promoDesc}>{headline}</p>}
                {discount && <p className={styles.promoSubline}>
                        {t.rich('promoOfferSubline', {
          discount,
          bold: chunks => <strong>{chunks}</strong>
        })}
                    </p>}
            </div>
        </div>;
};
const PromoCarousel: FC<{
  coupons: Voucher[];
  promoLabel: string;
}> = ({
  coupons,
  promoLabel
}) => {
  const isRTL = useLocale() === 'ar';
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  return <div className={styles.promoCarousel} data-sentry-component="PromoCarousel" data-sentry-source-file="PromoBanner.tsx">
            <div className={styles.navButtons}>
                <button type="button" className={styles.navBtn} onClick={() => swiper?.slidePrev()} aria-label="Previous">
                    <ChevronLeft size={20} data-sentry-element="ChevronLeft" data-sentry-source-file="PromoBanner.tsx" />
                </button>
                <button type="button" className={styles.navBtn} onClick={() => swiper?.slideNext()} aria-label="Next">
                    <ChevronRight size={20} data-sentry-element="ChevronRight" data-sentry-source-file="PromoBanner.tsx" />
                </button>
            </div>
            <Swiper className={styles.promoSwiper} modules={[Pagination]} dir={isRTL ? 'rtl' : 'ltr'} slidesPerView={1} spaceBetween={12} loop pagination={{
      clickable: true
    }} onSwiper={setSwiper} data-sentry-element="Swiper" data-sentry-source-file="PromoBanner.tsx">
                {coupons.map(coupon => <SwiperSlide key={coupon.code}>
                        <PromoCard coupon={coupon} promoLabel={promoLabel} />
                    </SwiperSlide>)}
            </Swiper>
        </div>;
};
const PromoBanner: FC<PromoBannerProps> = ({
  coupons,
  subscriptionApplicable,
  suppressCoupons = false,
  showCustomQuote = true,
  membershipOffer,
  onSubscriptionClick,
  onCustomQuoteClick
}) => {
  const t = useTranslations('workspace-details');
  const hasCoupons = !suppressCoupons && !!coupons?.length;
  if (!hasCoupons && !subscriptionApplicable && !showCustomQuote && !membershipOffer) {
    return null;
  }
  const promoLabel = t('promoCode');
  const showCarousel = hasCoupons && (coupons as Voucher[]).length > 1;
  return <div className={styles.banner} data-sentry-component="PromoBanner" data-sentry-source-file="PromoBanner.tsx">
            {hasCoupons && (showCarousel ? <PromoCarousel coupons={coupons as Voucher[]} promoLabel={promoLabel} /> : <PromoCard coupon={(coupons as Voucher[])[0]} promoLabel={promoLabel} />)}

            {membershipOffer}

            {!membershipOffer && subscriptionApplicable && <button type="button" className={styles.infoRow} onClick={onSubscriptionClick}>
                    <span className={styles.infoIcon}>
                        <Zap size={16} />
                    </span>
                    <span className={styles.infoText}>{t('subscriptionAvailable')}</span>
                    <span className={styles.infoAction}>{t('readMore')}</span>
                </button>}

            {showCustomQuote && <button type="button" className={styles.viewingRow} onClick={onCustomQuoteClick}>
                    <span className={styles.viewingCopy}>
                        <span className={styles.viewingTitle}>
                            {t('requestViewingTitle')}
                        </span>
                        <span className={styles.viewingDesc}>
                            {t('requestViewingText')}
                        </span>
                    </span>
                    <span className={styles.viewingCta}>{t('requestViewingCta')}</span>
                </button>}
        </div>;
};
export default PromoBanner;